import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type HeaderGroup,
} from '@tanstack/react-table';

import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/admin/EmptyState';
import { ErrorState } from '@/components/admin/ErrorState';
import { ApiError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { adminCopy } from '@/lib/copy';

import { ColumnFilter } from './ColumnFilter';
import { ColumnVisibilityMenu } from './ColumnVisibilityMenu';
import { DensityToggle } from './DensityToggle';
import type {
  DataTableProps,
  Density,
  PaginationChangePayload,
  PaginationOffsetState,
  PaginationState,
} from './types';

const SKELETON_CELL_WIDTHS = ['w-24', 'w-40', 'w-16', 'w-32', 'w-20'] as const;

function densityStorageKey(tableId: string): string {
  return `admin.table.density.${tableId}`;
}

function readStoredDensity(tableId: string): Density | null {
  try {
    const raw = localStorage.getItem(densityStorageKey(tableId));
    if (raw === 'comfortable' || raw === 'dense') return raw;
    return null;
  } catch {
    return null;
  }
}

function writeStoredDensity(tableId: string, density: Density): void {
  try {
    localStorage.setItem(densityStorageKey(tableId), density);
  } catch {
    // ignore
  }
}

const DENSITY_ROW_HEIGHT: Record<Density, string> = {
  comfortable: 'h-12',
  dense: 'h-9',
};

const DENSITY_CELL_PADDING: Record<Density, string> = {
  comfortable: 'px-4 py-3',
  dense: 'px-4 py-2',
};

export function DataTable<TRow>(props: DataTableProps<TRow>) {
  const {
    tableId,
    data,
    columns,
    loading,
    error,
    onRetry,
    pagination,
    onPaginationChange,
    sort,
    onSortChange,
    selectable,
    selectedRowIds,
    onSelectionChange,
    bulkActions,
    rowActions,
    onRowClick,
    isRowClickable,
    emptyStates,
    getRowId,
    hasActiveFilters,
    density: densityProp,
    onDensityChange,
    hideToolbar,
    toolbar,
    embedded,
    onTableReady,
  } = props;

  // The shell holds the toolbar, the table and the footer; only the table
  // scrolls sideways. Scrolling the shell instead would drag the filter
  // controls and the pagination row off-screen on a wide table.
  const wrapperClass = embedded ? '' : 'bg-surface rounded-lg border border-(--color-border)';
  // overflow-x-auto (not overflow-hidden) so a table whose total column-width
  // genuinely exceeds the viewport scrolls horizontally rather than wrapping
  // chips/dates mid-word inside cells. overflow-y stays hidden because row
  // scrolling lives at the page level.
  const scrollClass = 'overflow-x-auto overflow-y-hidden';

  const [uncontrolledDensity, setUncontrolledDensity] = useState<Density>(
    () => readStoredDensity(tableId) ?? 'comfortable',
  );
  const density = densityProp ?? uncontrolledDensity;
  const setDensity = useCallback(
    (next: Density) => {
      if (onDensityChange) {
        onDensityChange(next);
      } else {
        setUncontrolledDensity(next);
        writeStoredDensity(tableId, next);
      }
    },
    [onDensityChange, tableId],
  );

  useEffect(() => {
    if (!densityProp) {
      const stored = readStoredDensity(tableId);
      if (stored) setUncontrolledDensity(stored);
    }
  }, [tableId, densityProp]);

  const selectedSet = useMemo(() => new Set(selectedRowIds ?? []), [selectedRowIds]);

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
  });

  // Surface the TanStack Table instance to consumers that need it for chrome
  // they render outside the table card (e.g. ListCard's tools row hosting the
  // ColumnVisibilityMenu). The callback is stable between renders in practice;
  // we only re-fire when it changes identity.
  useEffect(() => {
    if (onTableReady) onTableReady(table);
  }, [onTableReady, table]);

  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;

  const showToolbar = !hideToolbar;

  if (loading) {
    return (
      <div className={wrapperClass}>
        <div className={scrollClass}>
          <table className="text-body-sm w-full border-collapse">
            <thead>
              <HeaderRow
                headerGroups={headerGroups}
                sort={sort}
                onSortChange={onSortChange}
                selectable={selectable}
                hasActions={!!rowActions}
                allSelected={false}
                someSelected={false}
                onToggleAll={() => undefined}
              />
            </thead>
            <tbody role="status" aria-label="Loading" aria-busy="true">
              {Array.from({ length: 8 }).map((_, r) => (
                <tr
                  key={r}
                  className={cn(
                    'border-b',
                    density === 'dense' ? 'border-neutral-100' : 'border-(--color-border)',
                    DENSITY_ROW_HEIGHT[density],
                  )}
                >
                  {selectable ? (
                    <td className={cn('w-12 pl-4', DENSITY_CELL_PADDING[density])}>
                      <Skeleton className="h-4 w-4 bg-neutral-200" />
                    </td>
                  ) : null}
                  {headerGroups[0]?.headers.map((header, c) => (
                    <td
                      key={header.id}
                      className={cn('text-neutral-900', DENSITY_CELL_PADDING[density])}
                    >
                      <Skeleton
                        className={cn(
                          'h-3 bg-neutral-200',
                          SKELETON_CELL_WIDTHS[(r + c) % SKELETON_CELL_WIDTHS.length],
                        )}
                      />
                    </td>
                  ))}
                  {rowActions ? (
                    <td className={cn('w-12 pr-4', DENSITY_CELL_PADDING[density])} />
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    // The availability cadence gate isn't a failure — the practitioner is
    // simply blocked until they fill in their days. Retrying re-403s, and the
    // (non-dismissible) cadence modal is the resolution path; once it's
    // satisfied CadenceRedirectMount invalidates and this clears itself. So:
    // gate copy, no Try again.
    const isCadenceGate = error instanceof ApiError && error.code === 'AVAILABILITY_OVERDUE';
    return (
      <div className={cn(wrapperClass, 'p-8')}>
        <ErrorState
          title={isCadenceGate ? 'Update your availability' : undefined}
          description={
            isCadenceGate
              ? 'Your clients stay hidden until your availability is up to date.'
              : error.message
          }
          onRetry={
            isCadenceGate
              ? undefined
              : (onRetry ??
                (() => {
                  window.location.reload();
                }))
          }
        />
      </div>
    );
  }

  const dataEmpty = data.length === 0;

  const hasBulkSelection = Boolean(selectable) && selectedSet.size > 0;

  return (
    <div className={wrapperClass}>
      {toolbar ? (
        <div className="flex items-start justify-between gap-3 border-b border-(--color-border)">
          <div className="min-w-0 flex-1">{toolbar}</div>
          {showToolbar ? (
            <div className="flex shrink-0 items-center gap-1 px-3 py-3">
              <DensityToggle density={density} onChange={setDensity} />
              <ColumnVisibilityMenu table={table} />
            </div>
          ) : null}
        </div>
      ) : null}

      {showToolbar && !toolbar ? (
        <div
          className={cn(
            'flex items-center justify-between gap-3 px-4 py-3',
            !dataEmpty && 'border-b border-(--color-border)',
          )}
        >
          <div className="flex min-h-9 items-center gap-3">
            {hasBulkSelection ? (
              <>
                <span className="text-meta-sm text-neutral-500">{selectedSet.size} selected</span>
                {bulkActions
                  ? bulkActions(data.filter((row) => selectedSet.has(getRowId(row))))
                  : null}
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            <DensityToggle density={density} onChange={setDensity} />
            <ColumnVisibilityMenu table={table} />
          </div>
        </div>
      ) : null}

      {toolbar && hasBulkSelection ? (
        <div className="bg-primary-50/40 flex items-center gap-3 border-b border-(--color-border) px-4 py-2">
          <span className="text-meta-sm text-neutral-700">{selectedSet.size} selected</span>
          {bulkActions ? bulkActions(data.filter((row) => selectedSet.has(getRowId(row)))) : null}
        </div>
      ) : null}

      {dataEmpty ? (
        hasActiveFilters ? (
          (emptyStates?.noResults ?? (
            <EmptyState.NoResults
              title={adminCopy.empty.noResults.title}
              description={adminCopy.empty.noResults.description}
            />
          ))
        ) : (
          (emptyStates?.noData ?? (
            <EmptyState.NoData
              title="No items yet"
              description="Create the first item to get started."
            />
          ))
        )
      ) : (
        <>
          <div className={scrollClass}>
            <table className="text-body-sm w-full border-collapse">
              <thead>
                <HeaderRow
                  headerGroups={headerGroups}
                  sort={sort}
                  onSortChange={onSortChange}
                  selectable={selectable}
                  hasActions={!!rowActions}
                  allSelected={tableRows.length > 0 && selectedSet.size === tableRows.length}
                  someSelected={selectedSet.size > 0 && selectedSet.size < tableRows.length}
                  onToggleAll={(checked) => {
                    if (!onSelectionChange) return;
                    if (checked) {
                      onSelectionChange(tableRows.map((r) => r.id));
                    } else {
                      onSelectionChange([]);
                    }
                  }}
                />
              </thead>
              <tbody>
                {tableRows.map((row) => {
                  const isSelected = selectedSet.has(row.id);
                  const rowClickable =
                    Boolean(onRowClick) && (isRowClickable?.(row.original) ?? true);
                  return (
                    <tr
                      key={row.id}
                      onClick={
                        rowClickable
                          ? () => {
                              onRowClick?.(row.original);
                            }
                          : undefined
                      }
                      onKeyDown={
                        rowClickable
                          ? (e) => {
                              // Only when the row itself has focus. Without this
                              // guard a keydown on a button inside the row bubbles
                              // here, gets preventDefault'd, and the button's own
                              // activation (its default action) never fires, so
                              // the row opens instead of the button acting.
                              if (e.target !== e.currentTarget) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRowClick?.(row.original);
                              }
                            }
                          : undefined
                      }
                      role={rowClickable ? 'button' : undefined}
                      tabIndex={rowClickable ? 0 : undefined}
                      className={cn(
                        'border-b transition-colors duration-150 ease-out',
                        density === 'dense' ? 'border-neutral-100' : 'border-(--color-border)',
                        DENSITY_ROW_HEIGHT[density],
                        'hover:bg-neutral-50',
                        isSelected &&
                          'bg-primary-50 hover:bg-primary-50 relative shadow-[inset_3px_0_0_var(--color-primary-500)]',
                        rowClickable && 'cursor-pointer',
                        rowClickable &&
                          'focus-visible:bg-primary-50/50 focus-visible:shadow-[inset_3px_0_0_var(--color-primary-500)] focus-visible:outline-none',
                      )}
                      aria-selected={isSelected}
                    >
                      {selectable ? (
                        <td
                          className={cn('w-12 pl-4', DENSITY_CELL_PADDING[density])}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(v) => {
                              if (!onSelectionChange) return;
                              if (v) {
                                onSelectionChange([...(selectedRowIds ?? []), row.id]);
                              } else {
                                onSelectionChange(
                                  (selectedRowIds ?? []).filter((id) => id !== row.id),
                                );
                              }
                            }}
                            aria-label="Select row"
                          />
                        </td>
                      ) : null}

                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'text-neutral-900',
                            DENSITY_CELL_PADDING[density],
                            // Atomic cells (chips, dates, statuses) opt in via
                            // `meta.cellNowrap` so they stay on one line. Wider
                            // tables scroll horizontally via the wrapper rather
                            // than wrapping cell content mid-word.
                            cell.column.columnDef.meta?.cellNowrap && 'whitespace-nowrap',
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}

                      {rowActions ? (
                        <td
                          className={cn('w-12 pr-4 text-right', DENSITY_CELL_PADDING[density])}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {rowActions(row.original)}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination && onPaginationChange ? (
            <PaginationFooter pagination={pagination} onChange={onPaginationChange} />
          ) : null}
        </>
      )}
    </div>
  );
}

// ---- Header row ----

interface HeaderRowProps<TRow> {
  headerGroups: HeaderGroup<TRow>[];
  sort: DataTableProps<TRow>['sort'];
  onSortChange: DataTableProps<TRow>['onSortChange'];
  selectable: DataTableProps<TRow>['selectable'];
  hasActions: boolean;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: (checked: boolean) => void;
}

function HeaderRow<TRow>({
  headerGroups,
  sort,
  onSortChange,
  selectable,
  hasActions,
  allSelected,
  someSelected,
  onToggleAll,
}: HeaderRowProps<TRow>) {
  return (
    <>
      {headerGroups.map((group) => (
        <tr key={group.id} className="h-11 border-b border-(--color-border) bg-neutral-50/60">
          {selectable ? (
            <th className="w-12 pl-4">
              <Checkbox
                checked={allSelected}
                data-state={someSelected && !allSelected ? 'indeterminate' : undefined}
                onCheckedChange={(v) => {
                  onToggleAll(v === true);
                }}
                aria-label="Select all rows"
              />
            </th>
          ) : null}

          {group.headers.map((header) => {
            const column = header.column;
            const sortable = column.columnDef.enableSorting !== false;
            const isActive = sort?.field === column.id;
            const direction = isActive ? sort.direction : null;
            const SortIcon =
              direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ArrowUpDown;
            const filter = column.columnDef.meta?.filter;
            // Header content is usually a plain string; fall back to the column id
            // when callers pass a render function so the popover heading stays
            // readable without forcing every column to declare a label.
            const rawHeader = column.columnDef.header;
            const headerLabel = typeof rawHeader === 'string' ? rawHeader : column.id;

            // Headers inherit the same nowrap intent as their cells — a
            // column that won't wrap its data shouldn't wrap its header
            // either ("NEEDS LEAD" reads better as one line than two).
            const cellNowrap = column.columnDef.meta?.cellNowrap;
            return (
              <th
                key={header.id}
                scope="col"
                aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                className={cn('px-4 text-left', cellNowrap && 'whitespace-nowrap')}
              >
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={!sortable || !onSortChange}
                    onClick={() => {
                      if (!sortable || !onSortChange) return;
                      if (!isActive) onSortChange({ field: column.id, direction: 'asc' });
                      else if (direction === 'asc')
                        onSortChange({ field: column.id, direction: 'desc' });
                      else onSortChange(null);
                    }}
                    className={cn(
                      'text-label-md tracking-label inline-flex items-center gap-1.5 font-semibold text-neutral-600 uppercase',
                      sortable && onSortChange && 'cursor-pointer hover:text-neutral-900',
                    )}
                  >
                    {flexRender(column.columnDef.header, header.getContext())}
                    {sortable ? (
                      <SortIcon className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" />
                    ) : null}
                  </button>
                  {filter ? <ColumnFilter config={filter} columnLabel={headerLabel} /> : null}
                </div>
              </th>
            );
          })}

          {hasActions ? <th aria-hidden="true" className="w-12" /> : null}
        </tr>
      ))}
    </>
  );
}

// ---- Pagination footer ----

interface PaginationFooterProps {
  pagination: PaginationState;
  onChange: (next: PaginationChangePayload) => void;
}

function PaginationFooter({ pagination, onChange }: PaginationFooterProps) {
  if (pagination.type === 'cursor') {
    return (
      <div className="text-meta-sm flex items-center justify-end gap-2 border-t border-(--color-border) px-4 py-3 text-neutral-500">
        <button
          type="button"
          disabled={!pagination.hasPrev}
          onClick={() => {
            onChange({ type: 'cursor', direction: 'prev' });
          }}
          aria-label="Previous page"
          className={cn(
            'flex h-8 items-center gap-1 rounded-md px-2 text-neutral-600',
            'hover:bg-neutral-100 hover:text-neutral-900',
            'disabled:pointer-events-none disabled:opacity-40',
          )}
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          Previous
        </button>
        <button
          type="button"
          disabled={!pagination.hasNext}
          onClick={() => {
            onChange({ type: 'cursor', direction: 'next' });
          }}
          aria-label="Next page"
          className={cn(
            'flex h-8 items-center gap-1 rounded-md px-2 text-neutral-600',
            'hover:bg-neutral-100 hover:text-neutral-900',
            'disabled:pointer-events-none disabled:opacity-40',
          )}
        >
          Next
          <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return <OffsetPaginationFooter pagination={pagination} onChange={onChange} />;
}

function OffsetPaginationFooter({
  pagination,
  onChange,
}: {
  pagination: PaginationOffsetState;
  onChange: (next: PaginationChangePayload) => void;
}) {
  const { page, pageSize, total } = pagination;
  const hasTotal = total >= 0;
  const totalPages = hasTotal ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = hasTotal ? Math.min(page * pageSize, total) : page * pageSize;

  return (
    <div className="text-meta-sm flex items-center justify-between gap-3 border-t border-(--color-border) px-4 py-3 text-neutral-500">
      <div>
        {hasTotal ? (
          <>
            Showing {from}–{to} of {total}
          </>
        ) : (
          <>Page {page}</>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Select
          value={String(pageSize)}
          onValueChange={(v) => {
            onChange({ type: 'offset', page: 1, pageSize: Number(v) });
          }}
        >
          <SelectTrigger className="h-8 w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[10, 25, 50, 100].map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => {
              onChange({ type: 'offset', page: page - 1, pageSize });
            }}
            aria-label="Previous page"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md text-neutral-600',
              'hover:bg-neutral-100 hover:text-neutral-900',
              'disabled:pointer-events-none disabled:opacity-40',
            )}
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          {totalPages !== null ? (
            <span>
              Page {page} of {totalPages}
            </span>
          ) : (
            <span>Page {page}</span>
          )}
          <button
            type="button"
            disabled={totalPages !== null && page >= totalPages}
            onClick={() => {
              onChange({ type: 'offset', page: page + 1, pageSize });
            }}
            aria-label="Next page"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md text-neutral-600',
              'hover:bg-neutral-100 hover:text-neutral-900',
              'disabled:pointer-events-none disabled:opacity-40',
            )}
          >
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
